import React, { useEffect, useState } from 'react';
import ChatLayout from '../../components/chat/ChatLayout';
import LoadingSpinner from '../../components/chat/LoadingSpinner';
import MessageTextField from '../../components/chat/MessageTextField';
import NotificationCard from '../../components/ibok-core/module/NotificationCard';
import useConversationReply from '../../hooks/conversation/useConversationReply';
import useCurrentConversation from '../../hooks/conversation/useCurrentConversation';
import MessageList from '../../components/chat/MessageList';
import useBrowserAuthMe from '../../hooks/useBrowserAuthMe';
import useMarkConversationSeen from '../../hooks/conversation/useMarkConversationSeen';
import { getParticipant } from '../../utils/conversationUtil';
import { truncate } from '../../utils/stringUtil';
import useClassifiedDetails from '../../hooks/useClassifiedDetails';
import './MessagePage.less';
import useMessageReplyQueue from '../../hooks/conversation/useMessageReplyQueue';
var SellerProfileLink = function (_a) {
    var sellerId = _a.sellerId, children = _a.children;
    var url = "/selger/" + sellerId;
    return (React.createElement("a", { href: url, className: "sellerprofile-link" }, children));
};
var ParticipantName = function (_a) {
    var _b;
    var _c = _a.participant, participant = _c === void 0 ? null : _c;
    if (!participant) {
        return React.createElement("h2", { className: "participant__name" }, "Henter navn...");
    }
    var name = (_b = participant.name) !== null && _b !== void 0 ? _b : 'Bruker har ikke navn';
    return (React.createElement(SellerProfileLink, { sellerId: participant.id },
        React.createElement("h1", { className: "heading--hidden" },
            "Samtale med ",
            name),
        React.createElement("h2", { className: "participant__name" }, truncate(name, 100))));
};
var ClassifiedDetails = function (_a) {
    var book = _a.book, classified = _a.classified;
    return (React.createElement("div", null,
        React.createElement("a", { href: "/bok/" + (book === null || book === void 0 ? void 0 : book.isbn) + "?focus_on=" + classified.id, className: "bookpage-link" },
            React.createElement("h3", null, book.title)),
        React.createElement("div", null,
            classified.price,
            " kr"),
        React.createElement("div", null, classified.hasVipps && React.createElement("a", { key: "vipps", href: "hjelp#vipps", className: "icon vipps nohover inline", title: "Selgeren st\u00F8tter betaling gjennom vipps" }, " "))));
};
var SkeletonHeader = function () { return (React.createElement("header", { className: "messages__header" },
    React.createElement("div", { className: "skeleton-background" },
        React.createElement("div", { className: "skeleton-details" },
            React.createElement("div", { className: "skeleton-classified-details" }),
            React.createElement("div", { className: "skeleton-image" }))))); };
var ConversationBody = function (_a) {
    var children = _a.children;
    return React.createElement("div", { className: "conversation__body" }, children);
};
var VIPPS_MESSAGE = 'Hei! Kan jeg betale med Vipps? Hvilket navn vil vises når jeg skal betale?';
var MessagePage = function (_a) {
    var match = _a.match;
    var slug = match.params.slug.toLowerCase();
    var _b = useState(''), draft = _b[0], setDraft = _b[1];
    var _c = useState(0), focusRequest = _c[0], setFocusRequest = _c[1];
    var user = useBrowserAuthMe().user;
    var _d = useCurrentConversation({ slug: slug }), isLoading = _d.isLoading, conversation = _d.conversation, error = _d.error;
    var messageReplyQueue = useMessageReplyQueue().messageReplyQueue;
    var replyToConversation = useConversationReply({ conversation: conversation }).replyToConversation;
    // Ensure the message is marked as seen
    useMarkConversationSeen({ conversation: conversation, user: user });
    var _e = (conversation || {}).messages, messages = _e === void 0 ? [] : _e;
    var participant = conversation && user ? getParticipant(conversation, user) : undefined;
    var isParticipantBanned = (participant === null || participant === void 0 ? void 0 : participant.error) === 'Brukeren har blitt blokkert av ibok';
    var classified = (conversation || {}).classified;
    var book = useClassifiedDetails(classified).book;
    var coverUrl = book === null || book === void 0 ? void 0 : book.imageUrl;
    var hasAllConversationDetails = coverUrl && book && classified && !isLoading;
    useEffect(function () {
        setDraft('');
    }, [slug]);
    var handleAskAboutVipps = function () {
        setDraft(function (currentDraft) { return (currentDraft.length > 0
            ? currentDraft + "\n" + VIPPS_MESSAGE
            : VIPPS_MESSAGE); });
        setFocusRequest(function (currentRequest) { return currentRequest + 1; });
    };
    // We should show only the temporary messages for the correct conversation
    var temporaryMessages = messageReplyQueue.filter(function (message) { return message.conversationSlug === slug; });
    return React.createElement(ChatLayout, null,
        !hasAllConversationDetails ? React.createElement(SkeletonHeader, null)
            : React.createElement("header", { className: "messages__header" },
                React.createElement(ParticipantName, { participant: participant }),
                React.createElement("div", { className: "classified-details" },
                    React.createElement(ClassifiedDetails, { book: book, classified: classified }),
                    React.createElement("div", { className: "conversationpreview__cover" }, !!coverUrl && React.createElement("img", { src: coverUrl, alt: book === null || book === void 0 ? void 0 : book.title })))),
        React.createElement(ConversationBody, null,
            isLoading && React.createElement(LoadingSpinner, { label: "Henter samtalen din", className: "conversation__body__loading" }),
            error && React.createElement("div", { className: "conversation__body__error" },
                React.createElement(NotificationCard, { type: "error", title: "Kunne ikke hente samtale" },
                    React.createElement("p", null, "Noe gikk galt da vi fors\u00F8kte \u00E5 hente samtalen din"),
                    React.createElement("p", null,
                        React.createElement("code", null, error.message)))),
            conversation && user && React.createElement(MessageList, { onSendRetry: replyToConversation, temporaryMessages: temporaryMessages, messages: messages, currentUser: user, conversationSlug: conversation.slug, onAskAboutVipps: handleAskAboutVipps })),
        isParticipantBanned && React.createElement("div", { className: "conversation__body__banned" },
            "Brukeren ", participant === null || participant === void 0 ? void 0 :
            participant.name,
            " har blitt deaktivert grunnet brudd p\u00E5 iBok sine brukervilk\u00E5r."),
        React.createElement(MessageTextField, { onSubmit: replyToConversation, isSubmitDisabled: isParticipantBanned, message: draft, onMessageChange: setDraft, focusRequest: focusRequest }));
};
export default MessagePage;
